import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {format, formatDistanceToNow} from 'date-fns';
import {ShieldCheckIcon, ShieldExclamationIcon} from '@heroicons/react/24/outline';
import {LockClosedIcon, LockOpenIcon, PencilSquareIcon, TrashIcon} from '@heroicons/react/24/outline';
import {IProfile, VerdocsEndpoint, deleteOrganizationMember, resetOrganizationMemberMFA, unlockOrganizationMember} from '@verdocs/js-sdk';
import {EditMemberDialog, LockMemberDialog, OkCancelDialog} from '../../../Dialogs';
import {Badge, Card, RowAction, SectionTitle, Spinner} from '../../../Components';
import {getCallerAccess, memberName, roleBadge} from './memberAccess';
import {useOrganizationMembers} from '../../../react-sdk/queries';
import {AppState} from '../../../AppState';

const initials = (member: IProfile) =>
  `${member.first_name?.charAt(0) || ''}${member.last_name?.charAt(0) || ''}` || member.email?.charAt(0).toUpperCase() || '?';

export const MembersTable: FC = observer(() => {
  const organizationId = AppState.currentProfile?.organization_id;
  const {profileId, isAdmin, isOwner, canManage} = getCallerAccess();

  const [working, setWorking] = useState(false);
  const [editing, setEditing] = useState<IProfile | null>(null);
  const [locking, setLocking] = useState<IProfile | null>(null);
  const [removing, setRemoving] = useState<IProfile | null>(null);
  const [unlocking, setUnlocking] = useState<IProfile | null>(null);
  const [resetting, setResetting] = useState<IProfile | null>(null);
  const membersQuery = useOrganizationMembers(organizationId);
  const members = membersQuery.data || [];
  const withMfa = members.filter((member) => member.user?.mfa_enabled).length;

  if (membersQuery.isLoading) {
    return <Spinner mode="dark" />;
  }

  if (membersQuery.isError) {
    return (
      <Card>
        <div className="text-[13px] text-gray-500">Unable to load organization members. Please try again later.</div>
      </Card>
    );
  }

  const handleRemove = async () => {
    const target = removing;
    if (!target) return;
    setRemoving(null);
    setWorking(true);
    try {
      await deleteOrganizationMember(VerdocsEndpoint.getDefault(), target.id);
      membersQuery.refetch().catch((e: any) => console.log('Unknown Error', e));
      toast.info('Member removed.');
    } catch (e: any) {
      console.log('Error deleting member', e);
      toast.error(e.response?.data?.error || 'Error removing member, please try again later.');
    }
    setWorking(false);
  };

  const handleResetMFA = async () => {
    const target = resetting;
    if (!target) return;
    setResetting(null);
    setWorking(true);
    try {
      await resetOrganizationMemberMFA(VerdocsEndpoint.getDefault(), target.id);
      membersQuery.refetch().catch((e: any) => console.log('Unknown Error', e));
      toast.success('Two-factor authentication reset.');
    } catch (e: any) {
      console.log('Error resetting MFA', e);
      toast.error(e.response?.data?.error || 'Error resetting two-factor authentication, please try again later.');
    }
    setWorking(false);
  };

  const handleUnlock = async () => {
    const target = unlocking;
    if (!target) return;
    setUnlocking(null);
    setWorking(true);
    try {
      await unlockOrganizationMember(VerdocsEndpoint.getDefault(), target.id);
      membersQuery.refetch().catch((e: any) => console.log('Unknown Error', e));
      toast.success('Member unlocked.');
    } catch (e: any) {
      console.log('Error unlocking member', e);
      toast.error(e.response?.data?.error || 'Error unlocking member, please try again later.');
    }
    setWorking(false);
  };

  return (
    <>
      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <SectionTitle className="mb-0">Members</SectionTitle>
          {isAdmin && members.length > 0 && (
            <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
              <ShieldCheckIcon className="h-4 w-4 text-[#1f7a4c]" />
              Two-factor on for {withMfa} of {members.length}
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#ebecf0] text-[11px] uppercase tracking-wide text-gray-400">
                <th className="py-1.5 text-left font-medium">Member</th>
                <th className="py-1.5 pl-3 text-left font-medium">Role</th>
                {isAdmin && <th className="py-1.5 pl-3 text-center font-medium">Two-factor</th>}
                {isAdmin && <th className="hidden py-1.5 pl-3 text-left font-medium tablet:table-cell">Last active</th>}
                <th className="py-1.5 pl-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const isSelf = member.id === profileId;
                const manageable = canManage(member);
                const role = roleBadge(member.roles);
                const user = member.user;

                return (
                  <tr key={member.id} className="border-b border-[#f2f3f5] last:border-0">
                    <td className="py-2.5">
                      <div className="flex items-center gap-3">
                        {member.picture ? (
                          <img
                            src={member.picture}
                            alt=""
                            className="hidden h-8 w-8 flex-shrink-0 rounded-full object-cover tablet:block"
                          />
                        ) : (
                          <div className="hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#efedfb] text-[11px] font-medium text-[#654dcb] tablet:flex">
                            {initials(member)}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-[#33364b]">
                            {[member.first_name, member.last_name].filter(Boolean).join(' ') || member.email}
                            {isSelf && <Badge tone="muted">You</Badge>}
                            {user?.locked && (
                              <Badge tone="warn" title={user.lock_reason || 'Account locked'}>
                                Locked
                              </Badge>
                            )}
                          </div>
                          <div className="truncate text-[12px] text-gray-500">{member.email}</div>
                        </div>
                      </div>
                    </td>

                    <td className="py-2.5 pl-3">{role && <Badge tone={role.tone}>{role.label}</Badge>}</td>

                    {isAdmin && (
                      <td className="py-2.5 pl-3 text-center">
                        {user?.mfa_enabled ? (
                          <ShieldCheckIcon
                            className="inline h-5 w-5 text-[#1f7a4c]"
                            aria-label="Two-factor on"
                            title={user.mfa_enrolled_at ? `On since ${format(new Date(user.mfa_enrolled_at), 'MMM d, yyyy')}` : 'On'}
                          />
                        ) : (
                          <ShieldExclamationIcon className="inline h-5 w-5 text-gray-300" aria-label="Two-factor off" title="Not set up" />
                        )}
                      </td>
                    )}

                    {isAdmin && (
                      <td className="hidden whitespace-nowrap py-2.5 pl-3 text-[12px] text-gray-500 tablet:table-cell">
                        {user?.last_active_at ? (
                          <span title={new Date(user.last_active_at).toLocaleString()}>
                            {formatDistanceToNow(new Date(user.last_active_at), {addSuffix: true})}
                          </span>
                        ) : (
                          <span className="text-gray-400">Never signed in</span>
                        )}
                      </td>
                    )}

                    <td className="py-2.5 pl-3">
                      {manageable && (
                        <div className="flex justify-end gap-0.5">
                          <RowAction title="Edit" onClick={() => setEditing(member)}>
                            <PencilSquareIcon />
                          </RowAction>
                          {user?.mfa_enabled && (
                            <RowAction title="Reset two-factor" onClick={() => setResetting(member)}>
                              <ShieldExclamationIcon />
                            </RowAction>
                          )}
                          {user &&
                            (user.locked ? (
                              <RowAction title="Unlock account" onClick={() => setUnlocking(member)}>
                                <LockOpenIcon />
                              </RowAction>
                            ) : (
                              <RowAction title="Lock account" onClick={() => setLocking(member)}>
                                <LockClosedIcon />
                              </RowAction>
                            ))}
                          <RowAction title="Remove from organization" danger onClick={() => setRemoving(member)}>
                            <TrashIcon />
                          </RowAction>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {resetting && (
        <OkCancelDialog
          title={`Reset two-factor for ${memberName(resetting)}?`}
          okLabel="Reset two-factor"
          onCancel={() => setResetting(null)}
          onOk={handleResetMFA}>
          <div className="mb-4 rounded-md border border-[#f0d9b5] bg-[#fdf8ef] p-3 text-[12px] text-[#95591a]">
            Please be sure you have positively identified the person for whom you are performing this reset.
            This is the primary target for phishing attacks. Are you sure it is really{' '}
            {resetting.first_name || memberName(resetting)} asking for this reset?
          </div>
          <div className="text-[13px] text-gray-500">
            {resetting.first_name || memberName(resetting)}'s authenticator and backup codes will be removed, 
            allowing them to sign in with just their password, then re-configure MFA.
          </div>
        </OkCancelDialog>
      )}

      {unlocking && (
        <OkCancelDialog title={`Unlock ${memberName(unlocking)}?`} okLabel="Unlock" onCancel={() => setUnlocking(null)} onOk={handleUnlock}>
          <div className="text-[13px] text-gray-500">
            {unlocking.user?.lock_reason ? `Locked: ${unlocking.user.lock_reason}. ` : ''}
            Unlock {unlocking.first_name || memberName(unlocking)} to allow them to sign in again?
          </div>
        </OkCancelDialog>
      )}

      {removing && (
        <OkCancelDialog title="Remove member" okLabel="Remove" onCancel={() => setRemoving(null)} onOk={handleRemove}>
          <div className="text-[13px] text-gray-500">
            {`Remove ${memberName(removing)} from the organization? Their templates and envelopes will be reassigned to you, and any API keys that act as them will be deleted. This action cannot be undone.`}
          </div>
        </OkCancelDialog>
      )}

      {locking && (
        <LockMemberDialog
          member={locking}
          onClose={(locked) => {
            setLocking(null);
            if (locked) {
              membersQuery.refetch().catch((e: any) => console.log('Unknown Error', e));
            }
          }}
        />
      )}

      {editing && (
        <EditMemberDialog
          member={editing}
          onClose={() => {
            setEditing(null);
            membersQuery.refetch();
          }}
        />
      )}

      {working && (
        <div className="absolute inset-0 bg-[#0000007f] flex flex-col items-center pt-8 text-white font-lg font-semibold">
          <Spinner mode="light" />
          <div className="mt-4">Please wait...</div>
        </div>
      )}
    </>
  );
});
